const STORAGE_KEY = 'postLoginReturnPath';

// The one page kept across a sign-in that leaves the SPA: the screen an MCP client sends the user to.
const REMEMBERED_PATHNAME = '/oauth-consent';

interface PathAndQuery {
  pathname: string;
  search: string;
}

/**
 * Called as a signed-out user is sent to sign in from `pathname`. An SSO sign-in leaves the SPA and comes back to
 * '/', which loses the `state.from` the login redirect carries, so the consent screen is also kept in sessionStorage.
 * Being sent to sign in from any other page drops a path kept before: a consent screen left unanswered cannot
 * capture a later sign-in.
 */
export const rememberPostLoginReturnPath = ({ pathname, search }: PathAndQuery): void => {
  try {
    if (pathname === REMEMBERED_PATHNAME) {
      window.sessionStorage.setItem(STORAGE_KEY, `${pathname}${search}`);
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // The browser refuses sessionStorage: an SSO sign-in then lands on '/', as it always did.
  }
};

/**
 * The remembered path a sign-in that just succeeded at `pathname` goes to, or null. It replaces '/' only: the
 * SSO callback's '/', or a login page with no `state.from` to return to. Read and removed on every sign-in, so a
 * remembered path serves at most one.
 */
export const consumePostLoginReturnPath = (pathToRedirectOnSuccess: string, pathname: string): string | null => {
  let rememberedPath: string | null;
  try {
    rememberedPath = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return null;
  }
  const landsOnRoot = pathToRedirectOnSuccess === '/' && (pathname === '/' || pathname.startsWith('/login'));

  return landsOnRoot ? rememberedPath : null;
};
